import { ref, computed, onUnmounted } from 'vue'

// Focus time = active seconds, not wall-clock span.
// We accumulate one second per tick ONLY while the window holds input focus
// AND there has been mouse/keyboard activity within IDLE_MS.
//   - i3: switching to another tiled window fires window 'blur' even though the
//     window stays visible, so blur/focus is the reliable "am I here" signal.
//   - IDLE_MS covers reading/thinking at the same window without input.
const IDLE_MS = 90_000        // pause after 90s of no input
const HEARTBEAT_MS = 20_000   // persist focus_seconds to server every 20s
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'wheel', 'scroll', 'touchstart']

export function useTimeTracking(api) {
  const activeSession = ref(null)
  const activeTaskId = ref(null)
  const elapsed = ref(0)        // accumulated FOCUS seconds (the displayed metric)
  const isPaused = ref(false)   // true while idle or window-blurred

  let tickTimer = null
  let heartbeatTimer = null
  let lastActivityAt = Date.now()
  let windowFocused = typeof document === 'undefined' ? true : document.hasFocus()

  const isTracking = computed(() => activeSession.value !== null)

  function markActivity() {
    lastActivityAt = Date.now()
  }
  function onBlur() {
    windowFocused = false
  }
  function onFocus() {
    windowFocused = true
    lastActivityAt = Date.now() // returning to the window counts as activity
  }

  function isActive() {
    return windowFocused && (Date.now() - lastActivityAt) < IDLE_MS
  }

  function attachListeners() {
    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, markActivity, { passive: true }))
    window.addEventListener('blur', onBlur)
    window.addEventListener('focus', onFocus)
  }
  function detachListeners() {
    ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, markActivity))
    window.removeEventListener('blur', onBlur)
    window.removeEventListener('focus', onFocus)
  }

  function startTimer() {
    stopTimer()
    windowFocused = document.hasFocus()
    lastActivityAt = Date.now()
    attachListeners()

    tickTimer = setInterval(() => {
      if (!activeSession.value) return
      const active = isActive()
      isPaused.value = !active
      if (active) elapsed.value += 1
    }, 1000)

    heartbeatTimer = setInterval(() => {
      if (activeSession.value) persistFocus(false)
    }, HEARTBEAT_MS)
  }

  function stopTimer() {
    if (tickTimer) { clearInterval(tickTimer); tickTimer = null }
    if (heartbeatTimer) { clearInterval(heartbeatTimer); heartbeatTimer = null }
    detachListeners()
    isPaused.value = false
  }

  async function persistFocus(stop) {
    if (!activeSession.value) return
    try {
      const data = await api.patch(
        `/api/tasks/${activeTaskId.value}/task_sessions/${activeSession.value.id}`,
        { focus_seconds: elapsed.value, stop }
      )
      return data.task_session
    } catch (e) {
      // network blip on a heartbeat is non-fatal; next heartbeat retries
      if (stop) throw e
    }
  }

  async function startSession(taskId) {
    if (activeSession.value) await stopSession()

    const data = await api.post(`/api/tasks/${taskId}/task_sessions`, {})
    activeSession.value = data.task_session
    activeTaskId.value = taskId
    elapsed.value = data.task_session.focus_seconds || 0
    startTimer()
    return data.task_session
  }

  async function stopSession() {
    if (!activeSession.value) return
    stopTimer()
    const result = await persistFocus(true)
    activeSession.value = null
    activeTaskId.value = null
    elapsed.value = 0
    return result
  }

  function hydrateActiveSession(session, taskId) {
    activeSession.value = session
    activeTaskId.value = taskId

    if (session) {
      // Resume from the focus seconds the server already has; the wall-clock
      // gap since started_at is NOT added (it was idle/away time).
      elapsed.value = session.focus_seconds || 0
      startTimer()
    } else {
      elapsed.value = 0
      stopTimer()
    }
  }

  function formatDuration(seconds) {
    const h = Math.floor(seconds / 3600)
    const m = Math.floor((seconds % 3600) / 60)
    if (h > 0) return `${h}h ${m}m`
    return `${m}m`
  }

  // On a hard tab-close mid-session we lose at most one heartbeat interval
  // (HEARTBEAT_MS) of focus seconds; the periodic PATCH covers everything else.
  onUnmounted(() => stopTimer())

  return {
    activeSession,
    activeTaskId,
    elapsed,
    isPaused,
    isTracking,
    startSession,
    stopSession,
    hydrateActiveSession,
    formatDuration,
  }
}
